import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/elements/Loader";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import { TRIP_PHASE, TRIP_STATUS } from "../../utils/constants";
import {
  getFormattedLocalTime,
  getFormattedLocalTimeWithMinusHour,
  getTimeDurationFor
} from "../../utils/dateUtils";
import AircraftDetails from "../Trip Details/views/AircraftDetails";
import AirportLocationInformation from "../Trip Details/views/AirportLocationInformation";
import TripID from "../Trip Details/views/TripID";
import "./css/AfterHoursDetails.css";
import AfterHoursActionButtons from "./views/AfterHoursActionButton";
import AfterHoursHeader from "./views/AfterHoursHeader";
import AfterHoursOfficersList from "./views/AfterHoursOfficersList";
import AfterHoursTiming from "./views/AfterHoursTiming";

const AfterHoursDetails = () => {
  const { getTripById, addAfterHoursOfficers } = useLandingAuthApi();
  const { tripId } = useParams();
  const [arrivalOfficers, setArrivalOfficers] = useState([]);
  const [departureOfficers, setDepartureOfficers] = useState([]);

  const { data: tripDetails, isLoading } = useQuery({
    queryKey: ["trip", tripId],
    queryFn: () => getTripById(tripId),
    refetchOnMount: "always",
  });
  const tripData = tripDetails?.data;

  useEffect(() => {
    if (tripData?.overtimeOfficers) {
      const filteredArrivalOfficers = tripData.overtimeOfficers.filter(
        (officer) => officer.tripPhase === TRIP_PHASE.ARRIVAL
      );
      const filteredDepartureOfficers = tripData.overtimeOfficers.filter(
        (officer) => officer.tripPhase === TRIP_PHASE.DEPARTURE
      );

      setArrivalOfficers(filteredArrivalOfficers);
      setDepartureOfficers(filteredDepartureOfficers);
    }
  }, [tripData?.overtimeOfficers]);

  const { mutate: addAfterHoursOfficersMutate } = useMutation({
    mutationFn: ({ tripId, tripPhase, data }) =>
      addAfterHoursOfficers(tripId, tripPhase, data),
    onSuccess: (response) => {},
  });

  const handleAddDepartureOfficer = (officers) => {
    const updatedOfficers = [...departureOfficers, ...officers.displayData];

    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: updatedOfficers,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers(updatedOfficers);
  };

  const handleAddArrivalOfficer = (officers) => {
    const updatedOfficers = [...arrivalOfficers, ...officers.displayData];

    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: updatedOfficers,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers(updatedOfficers);
  };

  const handleDeleteDepartureOfficer = (index) => {
    const updatedOfficers = departureOfficers.filter((_, i) => i !== index);
    setDepartureOfficers(updatedOfficers);

    // Prepare data for API call
    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.DEPARTURE,
      hoursWorked: officer.hoursWorked,
    }));

    // Call the add mutation with the updated departure officers list
    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: officersData,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
  };

  const handleDeleteArrivalOfficer = (index) => {
    const updatedOfficers = arrivalOfficers.filter((_, i) => i !== index);
    setArrivalOfficers(updatedOfficers);

    // Prepare data for API call
    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.ARRIVAL,
      hoursWorked: officer.hoursWorked,
    }));

    // Call the add mutation with the updated arrival officers list
    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: officersData,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
  };

  const handleEditDepartureOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...departureOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.DEPARTURE,
      hoursWorked: officer.hoursWorked,
    }));

    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: officersData,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers(updatedOfficers);
  };

  const handleEditArrivalOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...arrivalOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.ARRIVAL,
      hoursWorked: officer.hoursWorked,
    }));

    addAfterHoursOfficersMutate({
      tripId: tripData.id,
      data: officersData,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers(updatedOfficers);
  };

  const getDepartureTimingInfo = () => {
    if (!tripData?.requiresDepartureOvertime) return null;

    const openingTime = getFormattedLocalTimeWithMinusHour(
      tripData.departureTime
    );
    const departureTime = getFormattedLocalTime(tripData?.departureTime);
    const duration = getTimeDurationFor(openingTime, departureTime);

    return {
      openingTime: openingTime,
      scheduledTime: departureTime,
      durationDifferenceInHours: duration,
    };
  };

  const getArrivalTimingInfo = () => {
    if (!tripData?.requiresArrivalOvertime) return null;

    const closingTime = getFormattedLocalTimeWithMinusHour(
      tripData?.arrivalTime
    );
    const arrivalTime = getFormattedLocalTime(tripData?.arrivalTime);
    const duration = getTimeDurationFor(arrivalTime, closingTime);

    return {
      openingTime: closingTime,
      scheduledTime: arrivalTime,
      durationDifferenceInHours: duration,
    };
  };

  if (isLoading) {
    return <Loader />;
  }
  const isCancelled = tripData?.status === TRIP_STATUS.CANCELLED;

  return (
    <div className="after-hours-details-container">
      <AfterHoursHeader />

      {tripData?.requiresDepartureOvertime && (
        <>
          <div className="after-hours-departure-airport-name">
            <div className="after-hours-airport-name-status-row">
              <span>{tripData?.departureAirport?.name} (Departure)</span>
              {isCancelled && (
                <div className="after-hours-cancelled-button">
                  <span>{tripData?.status}</span>
                </div>
              )}
            </div>
          </div>

          <div className="after-hours-details-row">
            <div className="after-hours-details-left-column">
              <TripID id={tripData?.trackingId} />
              <AircraftDetails aircraft={tripData?.aircraft} />
              <AirportLocationInformation
                tripData={tripData}
                showStatus={false}
              />
            </div>
            <div className="after-hours-details-right-column">
              <AfterHoursTiming
                tripData={tripData}
                timingInfo={getDepartureTimingInfo()}
              />
              <AfterHoursOfficersList
                officers={departureOfficers}
                onAddOfficer={handleAddDepartureOfficer}
                onEditOfficer={handleEditDepartureOfficer}
                onDeleteOfficer={handleDeleteDepartureOfficer}
                clearanceStatus={tripData?.overtimeDepartureStatus}
                tripPhase={TRIP_PHASE.DEPARTURE}
                airportId={tripData?.departureAirport?.id}
                isAutoApprove={tripData?.isAutoCompleted}
              />
            </div>
          </div>
          <AfterHoursActionButtons
            tripId={tripId}
            tripData={tripData}
            officers={departureOfficers}
            clearanceStatus={tripData?.overtimeDepartureStatus}
            isArrival={false}
            isAutoApprove={tripData?.isAutoCompleted}
          />
        </>
      )}

      {tripData?.requiresArrivalOvertime && (
        <>
          <div className="after-hours-departure-airport-name">
            <div className="after-hours-airport-name-status-row">
              <span> {tripData?.arrivalAirport?.name} (Arrival)</span>
              {isCancelled && (
                <div className="after-hours-cancelled-button">
                  <span>{tripData?.status}</span>
                </div>
              )}
            </div>
          </div>
          <div className="after-hours-details-row">
            {!tripData?.requiresDepartureOvertime ? (
              <div className="after-hours-details-left-column">
                <TripID id={tripData?.trackingId} />
                <AircraftDetails aircraft={tripData?.aircraft} />
                <AirportLocationInformation
                  tripData={tripData}
                  showStatus={false}
                />
              </div>
            ) : (
              <AfterHoursTiming
                tripData={tripData}
                timingInfo={getArrivalTimingInfo()}
              />
            )}
            <div className="after-hours-details-right-column">
              {!tripData?.requiresDepartureOvertime && (
                <AfterHoursTiming
                  tripData={tripData}
                  timingInfo={getArrivalTimingInfo()}
                />
              )}
              <AfterHoursOfficersList
                officers={arrivalOfficers}
                onAddOfficer={handleAddArrivalOfficer}
                onEditOfficer={handleEditArrivalOfficer}
                onDeleteOfficer={handleDeleteArrivalOfficer}
                clearanceStatus={tripData?.overtimeArrivalStatus}
                tripPhase={TRIP_PHASE.ARRIVAL}
                airportId={tripData?.arrivalAirport?.id}
                isAutoApprove={tripData?.isAutoCompleted}
              />
            </div>
          </div>
          <AfterHoursActionButtons
            tripId={tripId}
            tripData={tripData}
            officers={arrivalOfficers}
            clearanceStatus={tripData?.overtimeArrivalStatus}
            isArrival={true}
            isAutoApprove={tripData?.isAutoCompleted}
          />
        </>
      )}
    </div>
  );
};

export default AfterHoursDetails;
